import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faCheck, faTimes, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { TextField, Button, Box, Typography } from '@mui/material';
import DetailsEditor from './DetailsEditor';
import GitHubAppInstallationShare from './GitHubAppInstallationShare';
import { useGitHubInstallations, useDeleteGitHubInstallation, useUpdateGitHubInstallation } from '../hooks/useGitHubInstallations';
import { useTenants } from '../hooks/useTenants';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import styles from './GitHubConfiguration.module.scss';
import '../styles/shared.scss';

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    name: string;
    description?: string;
    updatedAt: string;
}

interface ConnectionEditFormProps {
    editData: { name: string; description: string };
    setEditData: (data: { name: string; description: string }) => void;
    selectedInstallation: GitHubInstallation;
    onSave: () => Promise<void>;
    onDelete: () => Promise<void>;
    editorActions?: any;
    setEditorState?: (state: any) => void;
    tenants: any[];
}

const ConnectionEditForm: React.FC<ConnectionEditFormProps> = ({
    editData,
    setEditData,
    selectedInstallation,
    onSave,
    onDelete,
    editorActions,
    setEditorState,
    tenants
}) => {
    useEffect(() => {
        if (editorActions && setEditorState) {
            editorActions.onSave = onSave;
            editorActions.onDelete = onDelete;
            setEditorState({ saveEnabled: true, deleteEnabled: true });
        }
    }, [editorActions, setEditorState, onSave, onDelete]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                label="Connection Name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                fullWidth
                required
            />

            <TextField
                label="Description"
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
            />

            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Installation Details (Read-only)</Typography>

                <TextField
                    label="Account Login"
                    value={selectedInstallation.accountLogin}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Account Type"
                    value={selectedInstallation.accountType}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Repository Selection"
                    value={selectedInstallation.repositorySelection}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />

                <TextField
                    label="Installation ID"
                    value={selectedInstallation.installationId}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                />
            </Box>

            <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Tenant Shares</Typography>
                <GitHubAppInstallationShare
                    installation={selectedInstallation}
                    tenants={tenants}
                    onCancel={() => {}}
                    onSuccess={() => {}}
                />
            </Box>
        </Box>
    );
};

const GitHubAppManagement: React.FC = () => {
    const { data: installations = [], isLoading, error } = useGitHubInstallations();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();
    const deleteInstallation = useDeleteGitHubInstallation();
    const updateInstallation = useUpdateGitHubInstallation();
    const [selectedInstallation, setSelectedInstallation] = useState<GitHubInstallation | null>(null);
    const [editData, setEditData] = useState({ name: '', description: '' });

    const handleInstallationSelect = (installation: GitHubInstallation) => {
        setSelectedInstallation(installation);
        setEditData({ name: installation.name, description: installation.description || '' });
    };

    const handleDeleteInstallation = async (connectionId: string) => {
        try {
            await deleteInstallation.mutateAsync(connectionId);
            setSelectedInstallation(null);
        } catch (error) {
            console.error('Failed to delete installation:', error);
        }
    };

    const handleSave = async () => {
        if (!selectedInstallation) return;
        
        try {
            await updateInstallation.mutateAsync({
                connectionId: selectedInstallation.connectionId,
                updates: {
                    name: editData.name,
                    description: editData.description
                }
            });
            setSelectedInstallation(null);
        } catch (error) {
            console.error('Failed to update installation:', error);
        }
    };

    const handleCancel = () => {
        setSelectedInstallation(null);
    };

    const handleConnectGitHub = () => {
        const appSlug = process.env.REACT_APP_GITHUB_APP_SLUG || 'locomotive10-product-catalog-dev';
        const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
        const applicationName = process.env.REACT_APP_APPLICATION_NAME || 'servicecatalog';
        const domain = process.env.REACT_APP_DOMAIN || 'rainmaking.cloud';
        const callbackUrl = `https://www.${environment}.${applicationName}.${domain}/integrations/github/callback`;
        const state = encodeURIComponent(JSON.stringify({ name: 'My GitHub Connection', description: '' }));
        const installUrl = `https://github.com/apps/${appSlug}/installations/new?state=${state}&redirect_url=${encodeURIComponent(callbackUrl)}`;

        window.location.href = installUrl;
    };

    return (
        <div className={styles.container}>
            <div className={styles.sidebar} style={{ width: '300px' }}>
                <div className={styles.header}>
                    <div className="page-title-group">
                        <h3>GitHub App Connections</h3>
                        <button
                            onClick={() => openGlossary('GitHub App Connection')}
                            className="info-button"
                            title="View definition"
                        >
                            Info
                        </button>
                    </div>
                </div>
                <div>
                    {isLoading ? (
                        <p className={styles.statusText}>Loading connections...</p>
                    ) : error ? (
                        <p className={styles.errorText}>Error loading data</p>
                    ) : (
                        installations.map((installation) => (
                            <div
                                key={installation.connectionId}
                                onClick={() => handleInstallationSelect(installation)}
                                className={`${styles.connectionItem} ${selectedInstallation?.connectionId === installation.connectionId ? styles.selected : ''}`}
                            >
                                <div className={styles.connectionHeader}>
                                    <FontAwesomeIcon
                                        icon={faRobot}
                                        className={styles.accessMethodIcon}
                                    />
                                    <div className={styles.connectionName}>
                                        {installation.name}
                                    </div>
                                    <div className={`${styles.status} ${installation.isActive ? styles.active : styles.inactive}`}>
                                        <FontAwesomeIcon
                                            icon={installation.isActive ? faCheck : faTimes}
                                            style={{ marginRight: '4px' }}
                                        />
                                        {installation.isActive ? 'Active' : 'Inactive'}
                                    </div>
                                </div>
                                <div className={styles.connectionMeta}>
                                    <div>{installation.accountLogin}</div>
                                    <div>{installation.accountType}</div>
                                    {installation.description && <div>{installation.description}</div>}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className={styles.content}>
                    <Button
                        variant="contained"
                        onClick={handleConnectGitHub}
                        startIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                    >
                        Connect GitHub
                    </Button>
                </div>
            </div>

            <div className={styles.content}>
                {selectedInstallation ? (
                    <DetailsEditor
                        title={`GitHub App Connection - ${selectedInstallation.name}`}
                        showSave={true}
                        showDelete={true}
                        onComplete={(action) => {
                            if (action === 'delete') {
                                handleDeleteInstallation(selectedInstallation.connectionId);
                            } else {
                                handleCancel();
                            }
                        }}
                    >
                        <ConnectionEditForm
                            editData={editData}
                            setEditData={setEditData}
                            selectedInstallation={selectedInstallation}
                            onSave={handleSave}
                            onDelete={async () => {}} // Empty async function since delete is handled by DetailsEditor
                            tenants={tenants}
                        />
                    </DetailsEditor>
                ) : (
                    <div className={styles.placeholder}>
                        Select a GitHub App connection to view details
                    </div>
                )}
            </div>
            
            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default GitHubAppManagement;